import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { map, retry, switchMap } from 'rxjs/operators';

import { ApiQuery, Combine } from '../../core/api/entities/api-query.entity';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { RecordId } from '../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../core/records/entities/partial-record.class';
import { RecordRestrictions } from '../../core/records/helpers/restrictions/record-restrictions.helper';
import { ZoneRestrictions } from '../../core/records/helpers/restrictions/record-zone-restrictions.helper';
import { ShRecordService } from '../../core/records/services/record.service';
import { defaultSortInfo } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { defaultPaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ShAbstractToastService } from '../../toasts/services/toast.abstract.service';
import { PartialToast, Toast } from '../../toasts/toast.module';
import { ShRecordDownloadService } from '../../core/exports/services/record-download.service';

@Injectable()
export class ViewerSandboxService {
    constructor(
        private readonly exportService: ShRecordDownloadService,
        private readonly toastService: ShAbstractToastService,
        private readonly recordService: ShRecordService,
        private readonly http: ShAbstractHttpService
    ) {}

    addToast(toast: Toast): void {
        this.toastService.addToast(toast);
    }

    updateToast(toastId: number, toast: PartialToast): void {
        this.toastService.updateToast(toastId, toast);
    }

    getRecord(id: string): Observable<PartialRecord> {
        return this.recordService.getRecord(id);
    }

    getAccessRepresentation(id: string): Observable<PartialRecord> {
        const query: ApiQuery = new ApiQuery(
            [],
            Combine.And(RecordRestrictions.representationsOfRecord(id as RecordId), ZoneRestrictions.fullZoneSearch())
        );
        return this.recordService.getRecords(defaultPaginationInfo, defaultSortInfo, query).pipe(
            map(records => records.results.filter(record => record.metadata.Administrative?.IsAccess)),
            switchMap(records => {
                if (records.length === 0) {
                    return throwError('Failed to retrieve access representation.');
                }
                return of(records[0]);
            })
        );
    }

    getExportBlob(record: PartialRecord): Observable<Blob> {
        return this.exportService.getDownloadUrlForAccessRepresentation(record).pipe(
            retry(1),
            switchMap(url => {
                if (!url) {
                    return throwError('Failed to determine download url for record.');
                }
                return this.http.getExternalBlob(url);
            })
        );
    }

    public registerConsultation(fragmentId: RecordId): void {
        this.recordService.registerConsultation(fragmentId);
    }
}
